"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { trackAdminSettingsUpdated } from "@/lib/analytics-service";
import { getAppSettings, updateAppSettings, type AppSettings } from "@/lib/app-settings-service";
import { Check, Loader2, Settings } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

export function AppSettingsCard() {
  const { toast } = useToast();
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [supportEmailValue, setSupportEmailValue] = useState<string>("");
  const [showBetaBadgeValue, setShowBetaBadgeValue] = useState<boolean>(false);
  const [hasChanges, setHasChanges] = useState(false);

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const currentSettings = await getAppSettings();
      setSettings(currentSettings);
      setSupportEmailValue(currentSettings.supportEmail || "");
      setShowBetaBadgeValue(currentSettings.showBetaBadge ?? false);
      setHasChanges(false);
    } catch (error) {
      console.error("Failed to load app settings", error);
      toast({
        title: "Error",
        description: "Failed to load app settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSupportEmailChange = (value: string) => {
    setSupportEmailValue(value);
    const currentEmail = settings?.supportEmail || "";
    const currentBetaBadge = settings?.showBetaBadge ?? false;
    setHasChanges(value !== currentEmail || showBetaBadgeValue !== currentBetaBadge);
  };

  const handleShowBetaBadgeChange = (value: boolean) => {
    setShowBetaBadgeValue(value);
    const currentEmail = settings?.supportEmail || "";
    const currentBetaBadge = settings?.showBetaBadge ?? false;
    setHasChanges(supportEmailValue !== currentEmail || value !== currentBetaBadge);
  };

  const handleSave = async () => {

    // Validate email if provided
    if (supportEmailValue.trim() && supportEmailValue.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(supportEmailValue.trim())) {
        toast({
          title: "Invalid email",
          description: "Please enter a valid email address for support requests.",
          variant: "destructive",
        });
        return;
      }
    }

    try {
      setSaving(true);
      await updateAppSettings({
        supportEmail: supportEmailValue.trim() || undefined,
        showBetaBadge: showBetaBadgeValue,
      });

      trackAdminSettingsUpdated("app_settings");

      // Update local state directly with what was just saved.
      // Calling loadSettings() here can return a stale Firestore cache value
      // (before the server write propagates to the local cache), causing the
      // switch to snap back to its previous value and appearing broken.
      const savedEmail = supportEmailValue.trim() || undefined;
      setSettings(prev => ({
        ...prev,
        supportEmail: savedEmail,
        showBetaBadge: showBetaBadgeValue,
      }));
      setHasChanges(false);

      toast({
        title: "Settings updated",
        description: "Application settings have been updated successfully.",
      });
    } catch (error) {
      console.error("Failed to update app settings", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update app settings. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      setSupportEmailValue(settings.supportEmail || "");
      setShowBetaBadgeValue(settings.showBetaBadge ?? false);
      setHasChanges(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Application Settings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Application Settings
        </CardTitle>
        <CardDescription>
          Control application-wide settings that affect all users.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="support-email">Support Email</Label>
            <Input
              id="support-email"
              type="email"
              value={supportEmailValue}
              onChange={(e) => handleSupportEmailChange(e.target.value)}
              placeholder="support@example.com"
              disabled={saving}
            />
            <p className="text-sm text-muted-foreground">
              Email address where support requests from users will be sent. Leave empty to disable support requests.
            </p>
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="show-beta-badge">Show Beta Badge</Label>
              <p className="text-sm text-muted-foreground">
                Display the beta badge in the corners of the web app.
              </p>
            </div>
            <Switch
              id="show-beta-badge"
              checked={showBetaBadgeValue}
              onCheckedChange={handleShowBetaBadgeChange}
              disabled={saving}
            />
          </div>

          {settings && settings.supportEmail && (
            <Alert>
              <AlertDescription className="text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-primary" />
                    <span>
                      Support email: <strong>{settings.supportEmail}</strong>
                    </span>
                  </div>
                </div>
                {settings.updatedAt && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Last updated: {settings.updatedAt.toLocaleString()}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex items-center gap-2">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              className="min-w-[100px]"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
            {hasChanges && (
              <Button
                variant="outline"
                onClick={handleReset}
                disabled={saving}
              >
                Reset
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

